'use client';

import { Icon } from '@/src/components/ui';
import { Stopwatch } from '@/src/components/ui/Stopwatch';
import { IEconomyData } from '@/src/types/interfaces';
import { IUIProfile } from '@/src/types/IUIProfile';
import { Button } from '@workspace/ui/components/button';
import Link from 'next/link';

type Props = {
  isOpen: boolean;
  profile?: IUIProfile;
  targetIso: string | null;
  economyData?: IEconomyData | null;
};

export const UserProgressBlock = ({
  isOpen,
  profile,
  targetIso,
  economyData,
}: Props) => {
  const { giveSuperlikesPerDay, level2ThresholdXp, level3ThresholdXp } = economyData ?? {};
  const isAnonymous = profile?.type === 'anonymous';
  const used = profile?.superLikesUsed ?? 0;
  const clampedUsed = Math.min(Math.max(used, 0), giveSuperlikesPerDay ?? 3);
  const remaining = (giveSuperlikesPerDay ?? 3) - clampedUsed;
  const checkNextLevelXP = (level: number) => {
    switch (level) {
      case 1:
        return level2ThresholdXp ?? 100;
      case 2:
        return level3ThresholdXp ?? 300;
      default:
        return 300;
    }
  };

  if (!isOpen) {
    return (
      <div className="flex flex-col items-center md:mb-7 gap-6.5">
        <div className="flex flex-col items-center">
          <Icon name="logo-heart" className="size-5 mb-2" />
          <span className="text-sm font-semibold">
            {remaining}/{giveSuperlikesPerDay ?? 3}
          </span>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Icon name="lvl" className="w-6 h-2" />
          <Icon name={`${profile?.level ?? 0}`} className="h-4 w-2" />
        </div>

        <div className="flex flex-col items-center text-base font-bold font-grotesk">
          <span className="text-sm">TKN</span>
          <span>{profile?.tokens ?? 0}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto md:pb-7 pb-4 sticky bottom-0 bg-white">
      <div className="flex items-center gap-4 mb-7">
        <div className="flex flex-col items-center">
          <Icon name="logo-heart" className="size-5 mb-2" />
          <span className="text-sm font-semibold">
            {remaining}/{giveSuperlikesPerDay ?? 3}
          </span>
        </div>

        <div className="flex-1">
          <h2 className="text-xs! font-bold mb-2">Remaining Super Likes:</h2>
          <div className="flex gap-1">
            {Array.from({ length: remaining }).map((_, i) => (
              <Icon key={`full-${i}`} name="logo-heart" className="size-5" />
            ))}
            {Array.from({ length: clampedUsed }).map((_, i) => (
              <Icon key={`empty-${i}`} name="empty-heart" className="size-5" />
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-2">
          <Icon name="lvl" className="w-6 h-2" />
          <Icon name={`${profile?.level ?? 0}`} className="h-4 w-2" />
        </div>

        <div className="flex justify-center flex-col">
          <div className="flex items-center justify-between mb-2">
            <Stopwatch targetIso={targetIso} />
            <span className="text-xs">
              {profile?.currentXP ?? 0}/{checkNextLevelXP(profile?.level ?? 0)}{' '}
              <span className="font-bold ml-1">XP</span>
            </span>
          </div>

          <div className="w-[215px] h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-pink-accent transition-all duration-300"
              style={{
                width: isAnonymous
                  ? '0%'
                  : `${((profile?.currentXP ?? 0) / checkNextLevelXP(profile?.level ?? 0)) * 100}%`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center text-base font-bold font-grotesk">
            <span className="text-sm">TKN</span>
            <div className="flex items-center gap-2">
              {profile?.tokens ?? 0}
              <Icon name="tokens" className="size-3.5" />
            </div>
          </div>
        </div>
        <Link href='/economy-rules'>
        <Button
          variant="secondary"
          className="h-12 p-4 w-48 text-sm! font-bold font-grotesk"
        >
          {isAnonymous ? 'Learn More' : 'Need Tokens?'}
        </Button>
        </Link>
      </div>
    </div>
  );
};
